import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";

// Socket.IO client
import { io } from "socket.io-client";

// MUI components and hooks
import {
  Box,
  Button,
  Grid,
  useMediaQuery,
  Container,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Chip,
  Skeleton,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

// MUI icons
import WinIcon from "@mui/icons-material/EmojiEvents";
import HistoryIcon from "@mui/icons-material/History";
import IncreaseIcon from "@mui/icons-material/TrendingUp";
import BidIcon from "@mui/icons-material/Gavel";
import EyeIcon from "@mui/icons-material/RemoveRedEye";
import QuickBidIcon from "@mui/icons-material/LocalFireDepartment";
import LooseIcon from "@mui/icons-material/SentimentDissatisfied";

// Localization
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

// API calls
import { createBid, getMyBids } from "../../api/bids";

// Custom UI components
import CountdownTimer from "../../components/UI/CountdownTimer";
import MUIAlert from "../../components/UI/MUIAlert";

const MyBids = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [socket, setSocket] = useState(null);

  const [activeBids, setActiveBids] = useState([]);
  const [pastBids, setPastBids] = useState([]);
  const [wonBids, setWonBids] = useState([]);

  const [showPastBids, setShowPastBids] = useState(false);
  const [showWonBids, setShowWonBids] = useState(false);

  const [loading, setLoading] = useState(true);
  const [submittingQuickBid, setSubmittingQuickBid] = useState(false);

  const [error, setError] = useState(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertOpen(false);
  };

  const fetchBids = async () => {
    try {
      const data = await getMyBids();

      const active = data.filter((bid) => bid.status === "active");
      active.sort((a, b) => new Date(a.end_time) - new Date(b.end_time));
      setActiveBids(active);

      const completed = data.filter((bid) => bid.status === "completed");
      completed.sort((a, b) => new Date(b.end_time) - new Date(a.end_time));

      const won = completed.filter(
        (bid) =>
          Number(bid.highest_bid_amount) === Number(bid.user_bid_amount) &&
          Number(bid.highest_bid_amount) > 0
      );
      setWonBids(won);

      const past = completed.filter(
        (bid) => !won.some((wonBid) => wonBid.bid_id === bid.bid_id)
      );
      setPastBids(past);
      setLoading(false); // Set loading to false after data is fetched
    } catch (err) {
      setError(t("errors.err"));
      setLoading(false);
    }
  };

  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_URL, {
      transports: ["websocket"], // or ['polling', 'websocket'] if fallback needed
      withCredentials: true,
    });
    setSocket(socket);

    socket.on("connect", () => {});

    socket.on("new_bid", (data) => {
      setActiveBids((prevBids) => {
        return prevBids.map((bid) => {
          const updatedBid = { ...bid };
          if (bid.auction_id == data.auctionId) {
            if (data.bidAmount > (bid.highest_bid_amount || 0)) {
              updatedBid.highest_bid_amount = data.bidAmount;
            }
            if (bid.highest_bidder_id == data.userId) {
              updatedBid.user_bid_amount = data.bidAmount;
            }
          }
          return updatedBid;
        });
      });
    });

    socket.on("auction_ended", (data) => {
      fetchBids();
    });

    socket.on("auction_updated", (data) => {
      fetchBids();
    });

    fetchBids();
    return () => {
      socket.disconnect();
    };
  }, []);

  const handleQuickBid = async (auctionId, currentHighest) => {
    const newBidAmount = Number(currentHighest) + 50;
    setSubmittingQuickBid(true);
    try {
      await createBid(auctionId, newBidAmount);
      fetchBids();
      showAlert(t("my_bids.bid_success"));
      setSubmittingQuickBid(false);
    } catch (err) {
      showAlert(t("my_bids.bid_failure"), "error");
      setSubmittingQuickBid(false);
    }
  };

  const handleTogglePastBids = () => {
    setShowPastBids((prev) => !prev);
    setShowWonBids(false);
  };

  const handleToggleWonBids = () => {
    setShowWonBids((prev) => !prev);
    setShowPastBids(false);
  };

  return (
    <>
      <Helmet>
        <title>{t("seo.title.my_bids")}</title>
        <meta property="og:title" content={t("seo.title.my_bids")} />

        <meta name="robots" content="noindex" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Container
        maxWidth="lg"
        sx={{
          mt: {
            xs: 4,
            md: 10,
          },
          mb: {
            xs: 4,
            md: 12,
          },
        }}
      >
        <MUIAlert
          open={alertOpen}
          onClose={handleAlertClose}
          severity={alertSeverity}
          message={alertMessage}
        />

        <Typography color="primary" variant="h2" sx={{ pt: 4 }} gutterBottom>
          {t("my_bids.page_title")}
        </Typography>

        {error && <Typography color="error">{error}</Typography>}

        {loading ? ( // Show skeleton when loading is true
          <Paper sx={{ padding: 2, marginBottom: 2 }}>
            <List>
              {Array.from({ length: 3 }).map(
                (
                  _,
                  index // Show 3 skeletons
                ) => (
                  <ListItem key={index} sx={{ mb: 2 }}>
                    <ListItemText
                      primary={<Skeleton width="40%" />}
                      secondary={
                        <>
                          <Skeleton width="60%" />
                          <br />
                          <Skeleton width="50%" />
                          <br />
                          <Skeleton width="40%" />
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Skeleton width="100px" height="40px" />
                    </ListItemSecondaryAction>
                  </ListItem>
                )
              )}
            </List>
          </Paper>
        ) : activeBids.length > 0 ? (
          <>
            <Paper sx={{ padding: 2, marginBottom: 2 }}>
              <List>
                {activeBids.map((bid, index) => {
                  const userIsWinning =
                    Number(bid.highest_bid_amount) ===
                      Number(bid.user_bid_amount) &&
                    Number(bid.highest_bid_amount) > 0;

                  return (
                    <>
                      <ListItem
                        button
                        key={bid.bid_id}
                        component={Link}
                        to={`/izsole/${bid.auction_id}`}
                        sx={{
                          borderStyle: "solid",
                          borderWidth: "1px",
                          borderColor: "transparent",
                          mb: 1,
                          backgroundColor: userIsWinning
                            ? ""
                            : theme.palette.error.background2,
                          "&:hover": {
                            backgroundColor: userIsWinning
                              ? ""
                              : theme.palette.error.background,
                          },
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: 2,
                        }}
                      >
                        {/* LEFT: Property name + chip + countdown */}
                        <Box
                          sx={{ flex: 1, width: { xs: "100%", sm: "auto" } }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            {bid.property_name
                              ? bid.property_name
                              : `${t("general.auction")} ${bid.auction_id}`}

                            {userIsWinning && (
                              <Chip
                                sx={{
                                  ml: 1,
                                  px: 0.5,
                                  py: 0,
                                  backgroundColor:
                                    theme.palette.success.background2,
                                  borderStyle: "solid",
                                  borderWidth: "1px",
                                  borderColor: theme.palette.success.main,
                                  color: theme.palette.success.main,
                                  fontSize: "0.7em",
                                }}
                                icon={<WinIcon />}
                                label={t("my_bids.winning")}
                                size="small"
                                color="success"
                                variant="contained"
                              />
                            )}
                            {!userIsWinning && (
                              <Chip
                                sx={{
                                  ml: 1,
                                  px: 0.5,
                                  py: 0,
                                  backgroundColor:
                                    theme.palette.error.background,
                                  borderStyle: "solid",
                                  borderWidth: "1px",
                                  borderColor: theme.palette.error.main,
                                  color: theme.palette.error.main,
                                  fontSize: "0.7em",
                                }}
                                icon={<LooseIcon />}
                                label={t("my_bids.loosing")}
                                size="small"
                                color="success"
                                variant="contained"
                              />
                            )}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            <CountdownTimer endTime={bid.end_time} />
                          </Typography>
                        </Box>

                        {/* MIDDLE: Bids */}
                        <Box
                          sx={{
                            flex: 1,
                            width: { xs: "100%", sm: "auto" },
                            textAlign: { xs: "left", sm: "center" },
                          }}
                        >
                          <Typography variant="body1">
                            €{Number(bid.highest_bid_amount)} (
                            {t("my_bids.highest_bid")})
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            €{bid.user_bid_amount} ({t("my_bids.my_bid")})
                          </Typography>
                        </Box>

                        {/* RIGHT: Action Button */}
                        <Box
                          sx={{
                            width: { xs: "100%", sm: "auto" },
                            mt: { xs: 1, sm: 0 },
                          }}
                        >
                          {userIsWinning ? (
                            <Button
                              variant="outlined"
                              color="primary"
                              fullWidth={true}
                              component={Link}
                              to={`/izsole/${bid.auction_id}`}
                              sx={{ px: 2 }}
                              endIcon={<EyeIcon />}
                            >
                              {submittingQuickBid ? (
                                <CircularProgress size={24} color="inherit" />
                              ) : (
                                t("my_bids.open_auction")
                              )}
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="error"
                              onClick={() =>
                                handleQuickBid(
                                  bid.auction_id,
                                  bid.highest_bid_amount
                                )
                              }
                              fullWidth={true}
                              sx={{ fontWeight: "bold" }}
                              endIcon={<QuickBidIcon />}
                            >
                              {submittingQuickBid ? (
                                <CircularProgress size={24} color="inherit" />
                              ) : (
                                t("my_bids.quick_bid_pt1") +
                                " +" +
                                (Number(bid.highest_bid_amount) -
                                  Number(bid.user_bid_amount) +
                                  50) +
                                " " +
                                t("my_bids.quick_bid_pt2")
                              )}
                            </Button>
                          )}
                        </Box>
                      </ListItem>

                      {isMobile && index < activeBids.length - 1 && (
                        <Divider sx={{ my: 3 }} />
                      )}
                    </>
                  );
                })}
              </List>
            </Paper>
            <Box sx={{ display: "flex", gap: 1, mt: 2, mb: 2 }}>
              <Button
                variant="outlined"
                onClick={handleTogglePastBids}
                endIcon={<HistoryIcon />}
                sx={{
                  borderRadius: "50px",
                  color: showPastBids ? theme.palette.text.primary : "gray",
                  borderColor: "gray",
                }}
                disabled={loading} // Disable button while loading
              >
                {t("my_bids.bids_archive")}
              </Button>
              <Button
                variant="outlined"
                onClick={handleToggleWonBids}
                endIcon={<WinIcon />}
                sx={{
                  borderRadius: "50px",
                  color: showWonBids ? theme.palette.success.main : "gray",
                  borderColor: "gray",
                }}
                disabled={loading} // Disable button while loading
              >
                {t("my_bids.wins_archive")}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body1">
              {t("my_bids.no_active_bids")}
            </Typography>
            <Button
              variant="contained"
              component={Link}
              to="/visas_izsoles"
              color="success"
              endIcon={<BidIcon />}
              sx={{ mt: 4, mb: 3, color: "White" }}
            >
              {t("my_bids.no_bids_cta")}
            </Button>
          </>
        )}

        {showPastBids &&
          (loading ? ( // Skeleton for past bids
            <Paper sx={{ padding: 2, marginTop: 2, marginBottom: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("my_bids.bids_archive")}
              </Typography>
              <List>
                {Array.from({ length: 2 }).map((_, index) => (
                  <ListItem key={index} sx={{ mb: 1 }}>
                    <ListItemText
                      primary={<Skeleton width="40%" />}
                      secondary={<Skeleton width="60%" />}
                    />
                    <ListItemText
                      secondary={<Skeleton width="30%" />}
                      align="right"
                    />
                    <ListItemText
                      secondary={<Skeleton width="30%" />}
                      align="right"
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          ) : pastBids.length > 0 ? (
            <Paper sx={{ padding: 2, marginTop: 2, marginBottom: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("my_bids.bids_archive")}
              </Typography>
              <List>
                {pastBids.map((bid) => (
                  <ListItem
                    key={`${bid.bid_id}-${bid.auction_id}-past`}
                    button
                    onClick={() => navigate(`/izsole/${bid.auction_id}`)}
                    sx={{
                      mb: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.superlight,
                      },
                    }}
                  >
                    <ListItemText
                      primary={`${t("general.auction")}: ${bid.auction_id}`}
                      secondary={`${t("my_bids.end_time")}: ${new Date(
                        bid.end_time
                      ).toLocaleString()}`}
                    />
                    <ListItemText
                      secondary={`${t("my_bids.your_final_bid")}: €${Number(
                        bid.user_bid_amount
                      )}`}
                      align="right"
                    />
                    <ListItemText
                      secondary={`${t("my_bids.final_price")}: €${Number(
                        bid.highest_bid_amount
                      )}`}
                      align="right"
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          ) : (
            <Typography variant="body1">
              {t("my_bids.empty_archive")}
            </Typography>
          ))}

        {showWonBids &&
          (loading ? ( // Skeleton for won bids
            <Paper sx={{ padding: 2, marginTop: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("my_bids.wins_archive")}
              </Typography>
              <List>
                {Array.from({ length: 2 }).map((_, index) => (
                  <ListItem key={index} sx={{ mb: 1 }}>
                    <ListItemText
                      primary={<Skeleton width="40%" />}
                      secondary={<Skeleton width="60%" />}
                      sx={{ fontWeight: "bold" }}
                    />
                    <ListItemText
                      secondary={<Skeleton width="30%" />}
                      align="right"
                      sx={{ fontWeight: "bold" }}
                    />
                    <ListItemText
                      secondary={<Skeleton width="50%" />}
                      align="right"
                      sx={{
                        color: theme.palette.success.dark,
                        fontWeight: "bold",
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          ) : wonBids.length > 0 ? (
            <Paper sx={{ padding: 2, marginTop: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("my_bids.wins_archive")}
              </Typography>
              <List>
                {wonBids.map((bid) => (
                  <ListItem
                    key={`won-${bid.bid_id}-${bid.auction_id}`}
                    button
                    onClick={() => navigate(`/izsole/${bid.auction_id}`)}
                    sx={{
                      mb: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.superlight,
                      },
                    }}
                  >
                    <ListItemText
                      primary={`${t("general.auction")}: ${bid.auction_id}`}
                      secondary={`${t("my_bids.end_time")}: ${new Date(
                        bid.end_time
                      ).toLocaleString()}`}
                      sx={{ fontWeight: "bold" }}
                    />
                    <ListItemText
                      secondary={`${t("my_bids.final_price")}: €${Number(
                        bid.highest_bid_amount
                      )}`}
                      align="right"
                      sx={{ fontWeight: "bold" }}
                    />
                    <ListItemText
                      secondary={`🏆 ${t("my_bids.you_won")}`}
                      align="right"
                      sx={{
                        color: theme.palette.success.dark,
                        fontWeight: "bold",
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          ) : (
            <Typography variant="body1">{t("my_bids.no_wins")}</Typography>
          ))}

        {/* 🔁 Pulse animation */}
        <style>{`
                @keyframes bounce {
                    0%, 100% {
                        transform: scale(1);
                    }
                    50% {
                        transform: scale(1.02);
                    }
                }
            `}</style>
      </Container>
    </>
  );
};

export default MyBids;
